/**
 * AR / AP 沖帳記錄 schema(REQ-20260522 Phase 3B-Lite):
 * 從 Phase 3A 匯入的 ar_settlements / ap_settlements 對 UI 暴露,用於資料 review。
 * 業務模組(出貨單送出自動產 Receivable 等流程)留 Phase 3B 完整版。
 *
 * 手動登錄收款(建立 ArSettlement)於 REQ-20260807003 補上,見本檔末段。
 * 手動登錄付款(建立 ApSettlement)於 REQ-20260815002 補上,結構完全對稱。
 */
import { z } from 'zod';
import { nonNegativeDecimalString, positiveDecimalString } from './decimal.schema';

// ─── 共用 line schema(AR / AP 結構一致)─────────────────────

export const SettlementLineSchema = z.object({
  id: z.number().int(),
  sourceDocNo: z.string().nullable(),
  sourceDocType: z.string().nullable(),
  /** ISO date string,可能 null */
  sourceDate: z.string().nullable(),
  /** Decimal as string */
  currentBalance: z.string().nullable(),
  discountAmount: z.string().nullable(),
  settledAmount: z.string(),
  legacySeq: z.number().int().nullable(),
});
export type SettlementLine = z.infer<typeof SettlementLineSchema>;

// ─── AR(應收沖帳)─────────────────────────────────

export const ArSettlementListItemSchema = z.object({
  id: z.number().int(),
  documentNo: z.string(),
  subCode: z.number().int(),
  settledAt: z.string(),
  legacyPartyCode: z.string().nullable(),
  cashAmount: z.string().nullable(),
  checkAmount: z.string().nullable(),
  /** 支票兌現日期(REQ-20260817006)。ISO 字串,mdb 匯入的歷史沖帳一律 null */
  checkClearDate: z.string().nullable(),
  /** 匯款金額(REQ-20260817006) */
  transferAmount: z.string().nullable(),
  /** 匯款帳號末 4 碼(REQ-20260817006) */
  transferAccountLast4: z.string().nullable(),
  usePrepaid: z.string().nullable(),
  totalSettled: z.string(),
  receivableAfter: z.string().nullable(),
  prepaidAfter: z.string().nullable(),
  source: z.string(),
  lineCount: z.number().int().nonnegative(),
  /**
   * 解除沖銷(REQ-20260821001)。三個欄位一起看才知道這一列的角色:
   *  - `reversalOfId != null` → 這一列**本身就是**解除沖銷單(金額為負),不能再解除
   *  - `reversedByDocumentNo != null` → 這筆收款已經被那張解除沖銷單抵銷掉了
   *  - 兩個都是 null → 一般收款,可以解除
   */
  reversalOfId: z.number().int().nullable(),
  reversedByDocumentNo: z.string().nullable(),
  /** 解除原因(只有解除沖銷單自己有) */
  reversalReason: z.string().nullable(),
});
export type ArSettlementListItem = z.infer<typeof ArSettlementListItemSchema>;

export const ArSettlementDetailSchema = ArSettlementListItemSchema.extend({
  customerId: z.number().int(),
  customerFullName: z.string(),
  customerSimpleName: z.string().nullable(),
  addPrepaid: z.string().nullable(),
  salesDiscount: z.string().nullable(),
  cashDiscount: z.string().nullable(),
  createdAt: z.string(),
  lines: z.array(SettlementLineSchema),
});
export type ArSettlementDetail = z.infer<typeof ArSettlementDetailSchema>;

export const ArSettlementListResponseSchema = z.object({
  items: z.array(ArSettlementListItemSchema),
  total: z.number().int().nonnegative(),
  /** 該客戶開帳資料(放這方便前端不用另 query) */
  openingReceivable: z.string().nullable(),
  openingPrepayment: z.string().nullable(),
  /** 沖帳合計(所有 settlement 加總,非分頁後)*/
  totalSettledSum: z.string(),
});
export type ArSettlementListResponse = z.infer<typeof ArSettlementListResponseSchema>;

// ─── AP(應付沖帳)─────────────────────────────────

export const ApSettlementListItemSchema = z.object({
  id: z.number().int(),
  documentNo: z.string(),
  subCode: z.number().int(),
  settledAt: z.string(),
  legacyPartyCode: z.string().nullable(),
  cashAmount: z.string().nullable(),
  checkAmount: z.string().nullable(),
  usePrepaid: z.string().nullable(),
  totalSettled: z.string(),
  payableAfter: z.string().nullable(),
  prepaidAfter: z.string().nullable(),
  source: z.string(),
  lineCount: z.number().int().nonnegative(),
  /**
   * 解除沖銷(REQ-20261029002)。三個欄位的意義與應收側
   * `ArSettlementListItem` 的同名欄位完全相同(REQ-20260821001)。
   */
  reversalOfId: z.number().int().nullable(),
  reversedByDocumentNo: z.string().nullable(),
  reversalReason: z.string().nullable(),
});
export type ApSettlementListItem = z.infer<typeof ApSettlementListItemSchema>;

export const ApSettlementDetailSchema = ApSettlementListItemSchema.extend({
  supplierId: z.number().int(),
  supplierFullName: z.string(),
  supplierSimpleName: z.string().nullable(),
  addPrepaid: z.string().nullable(),
  purchaseDiscount: z.string().nullable(),
  cashDiscount: z.string().nullable(),
  createdAt: z.string(),
  lines: z.array(SettlementLineSchema),
});
export type ApSettlementDetail = z.infer<typeof ApSettlementDetailSchema>;

export const ApSettlementListResponseSchema = z.object({
  items: z.array(ApSettlementListItemSchema),
  total: z.number().int().nonnegative(),
  openingPayable: z.string().nullable(),
  openingPrepaid: z.string().nullable(),
  totalSettledSum: z.string(),
});
export type ApSettlementListResponse = z.infer<typeof ApSettlementListResponseSchema>;

// ─── 手動登錄收款(REQ-20260807003)──────────────────────

/**
 * 一筆沖銷明細。`sourceDocNo` 是**字串單號**而不是 id ——
 * `ar_settlement_lines.sourceDocNo` 對應 `sales_orders.orderNo` 但刻意不是 FK
 * (歷史 mdb 沖帳指向的舊單有些根本沒匯進來,強制 FK 會匯不進去)。
 * 金額由後端拿該單的未結餘額驗證,前端送什麼都不採信。
 */
export const CreateArSettlementLineSchema = z.object({
  sourceDocNo: z.string().trim().min(1, '請選擇要沖銷的單據').max(20),
  settledAmount: positiveDecimalString,
});
export type CreateArSettlementLineInput = z.infer<typeof CreateArSettlementLineSchema>;

/**
 * POST /customers/:cid/ar-settlements body。
 *
 * 收款方式金額(現金 / 支票 / 匯款 / 預收)都可留白 —— 它們是「錢怎麼進來的」的註記,
 * 沖帳合計一律由明細加總,不採用這幾欄。
 * **沒有折讓欄位**是刻意的:折讓走出貨單的 `allowanceAmount`
 * (`POST /sales-orders/:id/allowance`),兩邊都能扣未結餘額的話就會有兩份真相。
 */
export const CreateArSettlementSchema = z.object({
  /** 收款日期 YYYY-MM-DD(以台北日期存,月結對帳單的「本期收款」靠它分期) */
  settledAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '收款日期格式應為 YYYY-MM-DD'),
  cashAmount: nonNegativeDecimalString.optional().nullable(),
  checkAmount: nonNegativeDecimalString.optional().nullable(),
  /**
   * 支票兌現日期 YYYY-MM-DD(REQ-20260817006)。與 `checkAmount` **不互相要求** ——
   * 兌現日通常收票當下就寫在票面上,但也有先收票、日期後補的情況。
   */
  checkClearDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, '支票兌現日期格式應為 YYYY-MM-DD')
    .optional()
    .nullable(),
  /** 匯款(REQ-20260817006)。金額規則與現金 / 支票相同 */
  transferAmount: nonNegativeDecimalString.optional().nullable(),
  /** 匯款帳號末 4 碼(REQ-20260817006)。只有填了匯款金額才有意義,但不互相驗證 */
  transferAccountLast4: z
    .string()
    .regex(/^\d{4}$/, '匯款帳號末 4 碼應為 4 位數字')
    .optional()
    .nullable(),
  /** 動用客戶預收款 */
  usePrepaid: nonNegativeDecimalString.optional().nullable(),
  /** 本次多收、轉入預收款 */
  addPrepaid: nonNegativeDecimalString.optional().nullable(),
  lines: z.array(CreateArSettlementLineSchema).min(1, '至少要選一筆要沖銷的單據'),
});
export type CreateArSettlementInput = z.infer<typeof CreateArSettlementSchema>;

// ─── 手動登錄付款(REQ-20260815002)────────────────────────

/**
 * 一筆付款沖銷明細。與應收側逐字對稱 —— `ap_settlement_lines.sourceDocNo` 同樣是
 * **字串單號**對應 `purchase_orders.orderNo` 而不是 FK(理由見上方應收側的說明:
 * 歷史 mdb 沖帳指向的舊單有些沒匯進來)。金額由後端拿未結應付餘額驗證。
 */
export const CreateApSettlementLineSchema = z.object({
  sourceDocNo: z.string().trim().min(1, '請選擇要沖銷的單據').max(20),
  settledAmount: positiveDecimalString,
});
export type CreateApSettlementLineInput = z.infer<typeof CreateApSettlementLineSchema>;

/**
 * POST /suppliers/:sid/ap-settlements body。
 *
 * 欄位與 `CreateArSettlementSchema` 一一對應,只有語意方向相反
 * (預**付**款而不是預**收**款)。**同樣沒有折讓欄位**:折讓走進貨單的
 * `allowanceAmount`(`POST /purchase-orders/:id/allowance`),
 * `ApSettlement.purchaseDiscount` / `cashDiscount` 兩個 legacy 欄位刻意不開放 ——
 * 兩邊都能扣未結餘額的話就會有兩份真相(REQ-20260806002 §2.1 同一個理由)。
 */
export const CreateApSettlementSchema = z.object({
  /** 付款日期 YYYY-MM-DD(以台北日期存) */
  settledAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '付款日期格式應為 YYYY-MM-DD'),
  cashAmount: nonNegativeDecimalString.optional().nullable(),
  checkAmount: nonNegativeDecimalString.optional().nullable(),
  /** 動用付給廠商的預付款 */
  usePrepaid: nonNegativeDecimalString.optional().nullable(),
  /** 本次多付、轉入預付款 */
  addPrepaid: nonNegativeDecimalString.optional().nullable(),
  lines: z.array(CreateApSettlementLineSchema).min(1, '至少要選一筆要沖銷的單據'),
});
export type CreateApSettlementInput = z.infer<typeof CreateApSettlementSchema>;

// ─── 解除沖銷(REQ-20260821001)────────────────────────────

/**
 * POST /ar-settlements/:id/reverse body。
 *
 * 只要一個原因 —— 金額不給填:解除沖銷一律**整筆**反向,不做部分解除。
 * 部分解除等於「這筆收款其實只收了一部分」,那是登錄錯金額,
 * 正確作法是整筆解除後重新登錄一筆正確的收款(少一種會對不平的路徑)。
 *
 * 反向紀錄是**新增**一筆負金額的沖帳單,原始那筆收款與它的明細不會被改動
 * (REQ-20260807003 §五「比照反向沖帳單而不是刪除原紀錄」)。
 */
export const ReverseArSettlementSchema = z.object({
  reason: z.string().trim().min(1, '請填寫解除沖銷的原因').max(255),
});
export type ReverseArSettlementInput = z.infer<typeof ReverseArSettlementSchema>;

/**
 * POST /ap-settlements/:id/reverse body(REQ-20261029002)。
 * 規則與 `ReverseArSettlementSchema` 相同:只填原因,一律整筆反向。
 */
export const ReverseApSettlementSchema = z.object({
  reason: z.string().trim().min(1, '請填寫解除沖銷的原因').max(255),
});
export type ReverseApSettlementInput = z.infer<typeof ReverseApSettlementSchema>;
